import React, { useRef, useState } from "react";

const FilterBar = ({ showFilter, setShowFilter, filters, setFilters }) => {

    const allAmenities = [
        "Free WiFi", "AC", "TV", "Power Backup", "Parking",
        "Swimming Pool", "Gym", "Spa", "Restaurant", "Bar", "Laundry", "24x7 Room Service"
    ];

    const [showAllAmenities, setShowAllAmenities] = useState(false);
    const [isDragging, setIsDragging] = useState(false);

    const visibleAmenities = showAllAmenities ? allAmenities : allAmenities.slice(0, 5);

    const touchStartY = useRef(null);
    const touchCurrentY = useRef(null);

    const handleTouchStart = (e) => {
        touchStartY.current = e.touches[0].clientY;
    };

    const handleTouchMove = (e) => {
        touchCurrentY.current = e.touches[0].clientY;
    };

    const handleTouchEnd = () => {
        if (
            touchStartY.current !== null &&
            touchCurrentY.current !== null &&
            touchCurrentY.current - touchStartY.current > 100
        ) {
            setShowFilter(false);
        }
        touchStartY.current = null;
        touchCurrentY.current = null;
    };

    const handlePriceChange = (value) => {
        setFilters(prev => ({ ...prev, price: value }));
    };

    const handleAmenityChange = (amenity) => {
        setFilters(prev => ({
            ...prev,
            amenities: prev.amenities.includes(amenity)
                ? prev.amenities.filter(a => a !== amenity)
                : [...prev.amenities, amenity]
        }));
    };

    const handleRatingChange = (rating) => {
        setFilters(prev => ({ ...prev, rating: prev.rating === rating ? null : rating }));
    };

    const handleAccommodationTypeChange = (type) => {
        setFilters(prev => ({
            ...prev,
            accommodationType: prev.accommodationType.includes(type)
                ? prev.accommodationType.filter(t => t !== type)
                : [...prev.accommodationType, type]
        }));
    };

    const clearFilters = () => {
        setFilters({
            price: 5000,
            amenities: [],
            rating: null,
            accommodationType: [],
            coupleFriendly: false,
            petsAllowed: false,
        });
    };

    const FilterContent = () => (
        <div className="space-y-6">
            {/* Price Range */}
            <div className="filter-section">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                        <svg className="w-5 h-5 mr-2 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.51-1.31c-.562-.649-1.413-1.076-2.353-1.253V5z" clipRule="evenodd" />
                        </svg>
                        Price Range
                    </h3>
                    <button
                        onClick={clearFilters}
                        className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                    >
                        Clear All
                    </button>
                </div>

                <div className="relative mb-4">
                    {isDragging && (
                        <div
                            className="absolute -top-8 bg-gray-900 text-white px-2 py-1 rounded text-xs font-medium transform -translate-x-1/2"
                            style={{ left: `${((filters.price - 500) / (5000 - 500)) * 100}%` }}
                        >
                            ₹{filters.price}
                        </div>
                    )}
                    <input
                        type="range"
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                        min="500"
                        max="5000"
                        step="100"
                        value={filters.price}
                        onChange={(e) => handlePriceChange(e.target.value)}
                        onMouseDown={() => setIsDragging(true)}
                        onMouseUp={() => setIsDragging(false)}
                        onTouchStart={() => setIsDragging(true)}
                        onTouchEnd={() => setIsDragging(false)}
                    />
                </div>

                <div className="flex justify-between text-sm text-gray-600">
                    <span>₹500</span>
                    <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-md font-medium">₹{filters.price}</span>
                    <span>₹5000+</span>
                </div>
            </div>

            {/* Amenities */}
            <div className="filter-section">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                        <svg className="w-5 h-5 mr-2 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        Amenities
                    </h3>
                </div>

                <div className="space-y-3">
                    {visibleAmenities.map((amenity, i) => (
                        <label key={i} className="flex items-center cursor-pointer group">
                            <input
                                type="checkbox"
                                checked={filters.amenities.includes(amenity)}
                                onChange={() => handleAmenityChange(amenity)}
                                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                            />
                            <span className="ml-3 text-sm text-gray-700 group-hover:text-gray-900">{amenity}</span>
                        </label>
                    ))}
                </div>

                {allAmenities.length > 5 && (
                    <button
                        className="mt-3 text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center"
                        onClick={() => setShowAllAmenities(!showAllAmenities)}
                    >
                        {showAllAmenities ? "View Less" : "View More"}
                        <svg className={`w-4 h-4 ml-1 transform transition-transform ${showAllAmenities ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>
                )}
            </div>

            {/* Ratings */}
            <div className="filter-section">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                        <svg className="w-5 h-5 mr-2 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                        Guest Rating
                    </h3>
                </div>

                <div className="space-y-3">
                    {[5, 4, 3].map((star) => (
                        <label key={star} className="flex items-center cursor-pointer group">
                            <input
                                type="checkbox"
                                name="rating"
                                checked={filters.rating === star}
                                onChange={() => handleRatingChange(star)}
                                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                            />
                            <span className="ml-3 text-sm text-gray-700 group-hover:text-gray-900 flex items-center">
                                {star}
                                <svg className="w-4 h-4 ml-1 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                </svg>
                                & above
                            </span>
                        </label>
                    ))}
                </div>
            </div>

            {/* Accommodation Type */}
            <div className="filter-section">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                        <svg className="w-5 h-5 mr-2 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-6a1 1 0 00-1-1H9a1 1 0 00-1 1v6a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 8a1 1 0 011-1h1a1 1 0 011 1v1a1 1 0 01-1 1H8a1 1 0 01-1-1v-1z" clipRule="evenodd" />
                        </svg>
                        Property Type
                    </h3>
                </div>

                <div className="space-y-3">
                    {['Hotel', 'Resort', 'Villa', 'Apartment'].map((type) => (
                        <label key={type} className="flex items-center cursor-pointer group">
                            <input
                                type="checkbox"
                                checked={filters.accommodationType.includes(type)}
                                onChange={() => handleAccommodationTypeChange(type)}
                                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                            />
                            <span className="ml-3 text-sm text-gray-700 group-hover:text-gray-900">{type}</span>
                        </label>
                    ))}
                </div>
            </div>

            {/* Policies */}
            <div className="filter-section">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                        <svg className="w-5 h-5 mr-2 text-rose-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                        </svg>
                        Policies
                    </h3>
                </div>

                <div className="space-y-4">
                    <label className="flex items-center justify-between cursor-pointer group">
                        <span className="text-sm text-gray-700 group-hover:text-gray-900">Couple Friendly</span>
                        <div
                            onClick={() => setFilters(prev => ({ ...prev, coupleFriendly: !prev.coupleFriendly }))}
                            className={`relative w-11 h-6 rounded-full transition-colors duration-200 cursor-pointer ${filters.coupleFriendly ? 'bg-blue-600' : 'bg-gray-300'
                                }`}
                        >
                            <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${filters.coupleFriendly ? 'translate-x-5' : ''
                                }`} />
                        </div>
                    </label>

                    <label className="flex items-center justify-between cursor-pointer group">
                        <span className="text-sm text-gray-700 group-hover:text-gray-900">Pets Allowed</span>
                        <div
                            onClick={() => setFilters(prev => ({ ...prev, petsAllowed: !prev.petsAllowed }))}
                            className={`relative w-11 h-6 rounded-full transition-colors duration-200 cursor-pointer ${filters.petsAllowed ? 'bg-blue-600' : 'bg-gray-300'
                                }`}
                        >
                            <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${filters.petsAllowed ? 'translate-x-5' : ''
                                }`} />
                        </div>
                    </label>
                </div>
            </div>
        </div>
    );

    return (
        <>
            {/* Desktop Sidebar */}
            <aside className="hidden lg:block w-80 h-fit sticky top-6">
                <div className="px-4 py-2">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-xl font-bold text-gray-900">Filters</h2>
                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium">
                            {filters.amenities.length + filters.accommodationType.length + (filters.rating ? 1 : 0) + (filters.coupleFriendly ? 1 : 0) + (filters.petsAllowed ? 1 : 0) + (filters.price < 5000 ? 1 : 0)} active
                        </span>
                    </div>
                    <FilterContent />
                </div>
            </aside>

            {/* Mobile Bottom Sheet */}
            {showFilter && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 lg:hidden">
                    <div
                        className="fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl max-h-[90vh] overflow-hidden"
                        style={{
                            animation: "slideUp 0.3s ease-out"
                        }}
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                    >
                        {/* Handle bar */}
                        <div className="w-12 h-1.5 bg-gray-300 rounded-full mx-auto mt-3 mb-4"></div>

                        {/* Header */}
                        <div className="flex justify-between items-center px-6 pb-4 border-b border-gray-200">
                            <h2 className="text-xl font-bold text-gray-900">Filters</h2>
                            <button
                                className="p-2 hover:bg-gray-100 rounded-full transition-colors duration-200"
                                onClick={() => setShowFilter(false)}
                            >
                                <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Content */}
                        <div className="overflow-y-auto px-6 py-4" style={{ maxHeight: 'calc(90vh - 200px)' }}>
                            <FilterContent />
                        </div>

                        {/* Footer */}
                        <div className="border-t border-gray-200 p-4 bg-white">
                            <div className="flex gap-3">
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default FilterBar;
